import React, { useMemo, useState } from 'react'
import { Avatar, Box, Dialog, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import { type GridColDef } from '@mui/x-data-grid-pro'
import { useModal } from '../../../../../store/ModalProvider'
import CustomButton from '../../CustomButton/CustomButton'
import { AnimatedDataGridPro } from '../../CustomDataGrid'
import { dataGridDensityTransitionSx } from '../../CustomDataGrid/dataGridDensityTransitionSx'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import { popupActionButtonSx } from '../popupButtonStyles'
import { GRID_LOCALE_TEXT } from '../../../../../utils/tableLocaleText'
import type { RestAfterWorkModalData, RestAfterWorkRow } from '../../../../../types/modal'

type DensityMode = 'compact' | 'standard'

const dialogShadow =
	'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)'

const driverAvatarSrc = 'http://localhost:3845/assets/2e5f78f44b20ef67ab74659b2552d4e86c81968d.png'

const toggleGroupSx = {
	border: '1px solid rgba(134, 149, 175, 0.15)',
	borderRadius: '4px',
	backgroundColor: '#FFFFFF',
	'& .MuiToggleButton-root': {
		border: 'none',
		borderRadius: 0,
		padding: '4.8px',
		'&:not(:last-child)': {
			borderRight: '0.6px solid rgba(134, 149, 175, 0.15)',
		},
		'&.Mui-selected': {
			backgroundColor: '#F5F7FC',
			'&:hover': {
				backgroundColor: '#F5F7FC',
			},
		},
	},
	'& .MuiToggleButton-root:first-of-type': {
		borderTopLeftRadius: '4px',
		borderBottomLeftRadius: '4px',
	},
	'& .MuiToggleButton-root:last-of-type': {
		borderTopRightRadius: '4px',
		borderBottomRightRadius: '4px',
	},
}

const tableSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		fontFamily: 'Poppins, sans-serif',
		fontWeight: 600,
		fontSize: '12px',
		color: '#8695AF',
		letterSpacing: '0.17px',
		lineHeight: '24px',
		textTransform: 'none',
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
		color: 'rgba(134, 149, 175, 0.6)',
		transition: 'opacity 0.2s ease',
	},
	'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
		opacity: 1,
	},
	'& .MuiDataGrid-columnSeparator--resizable': {
		cursor: 'col-resize',
	},
	'& .MuiDataGrid-columnHeader': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '12px',
		color: '#092056',
		borderBottom: 'none',
		borderRight: 'none',
	},
	'& .MuiDataGrid-row': {
		borderBottom: 'none',
	},
	'& .MuiDataGrid-row:hover': {
		backgroundColor: 'rgba(0, 165, 255, 0.06)',
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
	'& .MuiTablePagination-displayedRows': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '11px',
		fontWeight: 400,
		lineHeight: '130%',
	},
	'& .MuiTablePagination-selectLabel': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '11px',
		fontWeight: 400,
		lineHeight: '130%',
	},
	'& .MuiTablePagination-selectIcon': {
		color: '#00A5FF',
	},
	'& .MuiTablePagination-actions .MuiSvgIcon-root': {
		color: '#00A5FF',
	},
}

const defaultRows: RestAfterWorkRow[] = Array.from({ length: 13 }, (_val, index) => ({
	id: `rest-${index + 1}`,
	motorista: 'Alexandre Gomes',
	nr: '0137',
	nrMec: '0137',
	filial: 'Transol',
	ultimaFolgaTrab: '02/05/2024',
	descansoSemanal: '',
	suficiente: '',
	descanso: '1',
	idZona: '',
	zona: 'Portim�o',
	tipoTrabalho: '',
	ultimoServico: '',
	avatarUrl: driverAvatarSrc,
}))

const CustomModalRestAfterWork: React.FC = () => {
	const { isOpen, closeModal, modalData } = useModal()
	const data = modalData as RestAfterWorkModalData | undefined

	const rows = useMemo(() => (data?.rows?.length ? data.rows : defaultRows), [data])
	const totalRows = data?.totalRows ?? rows.length
	const [density, setDensity] = useState<DensityMode>('compact')
	const [paginationModel, setPaginationModel] = useState({
		page: 0,
		pageSize: data?.rowsPerPage ?? 10,
	})

	const columns = useMemo<GridColDef<RestAfterWorkRow>[]>(
		() => [
			{
				field: 'motorista',
				headerName: 'Motorista',
				minWidth: 200,
				flex: 1.4,
				renderCell: params => {
					const name = String(params.row.motorista ?? '')
					const initial = name.trim().charAt(0) || 'M'

					return (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<Avatar
								src={params.row.avatarUrl}
								sx={{
									borderRadius: '100%',
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									justifyContent: 'center',
									bgcolor: 'lightgrey 50% / cover no-repeat',
									width: 32,
									height: 32,
									fontSize: '20px',
									fontWeight: 400,
									lineHeight: 1,
								}}
							>
								{initial}
							</Avatar>
							<Typography
								sx={{
									fontFamily: 'Poppins, sans-serif',
									fontSize: '12px',
									lineHeight: 1.3,
									color: '#092056',
								}}
							>
								{name}
							</Typography>
						</Box>
					)
				},
			},
			{ field: 'nr', headerName: 'Nr', width: 60 },
			{ field: 'nrMec', headerName: 'Nr Mec', width: 70 },
			{ field: 'filial', headerName: 'Filial', width: 80 },
			{
				field: 'ultimaFolgaTrab',
				headerName: '�ltima Folga Trab.',
				width: 140,
			},
			{
				field: 'descansoSemanal',
				headerName: 'Descanso Semanal',
				width: 135,
			},
			{ field: 'suficiente', headerName: 'Suficiente', width: 95 },
			{ field: 'descanso', headerName: 'Descanso', width: 90 },
			{ field: 'idZona', headerName: 'ID Zona', width: 80 },
			{ field: 'zona', headerName: 'Zona', width: 110 },
			{
				field: 'tipoTrabalho',
				headerName: 'Tipo Trabalho',
				width: 120,
			},
			{ field: 'ultimoServico', headerName: '�lt. Serv.', width: 90 },
		],
		[],
	)

	return (
		<Dialog
			open={isOpen}
			onClose={closeModal}
			maxWidth={false}
			sx={{ pointerEvents: 'auto', zIndex: 2000 }}
			PaperProps={{
				sx: {
					width: 'min(1200px, 96vw)',
					borderRadius: '6px',
					backgroundColor: '#FFFFFF',
					boxShadow: dialogShadow,
					overflow: 'hidden',
					zIndex: 2001,
				},
			}}
		>
			<Box sx={{ fontFamily: 'Poppins, sans-serif', color: '#092056' }}>
				<Box sx={{ padding: '16px 24px 8px' }}>
					<Typography
						sx={{
							fontSize: '14px',
							fontWeight: 600,
							lineHeight: 1.6,
							color: '#092056',
						}}
					>
						Descansos ap�s Folga / Complementar Trabalhada
					</Typography>
				</Box>

				<Box sx={{ padding: '0 24px 16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
						}}
					>
						<Typography
							sx={{
								fontSize: '20px',
								fontWeight: 600,
								lineHeight: 1.3,
								color: '#092056',
							}}
						>
							T�tulo
						</Typography>
						<ToggleButtonGroup
							value={density}
							exclusive
							onChange={(_event, value) => value && setDensity(value)}
							size='small'
							sx={toggleGroupSx}
						>
							<ToggleButton
								value='compact'
								aria-label='densidade compacta'
							>
								<TableRowsIcon
									sx={{
										width: '14.4px',
										height: '14.4px',
										color: density === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
									}}
								/>
							</ToggleButton>
							<ToggleButton
								value='standard'
								aria-label='densidade espa�ada'
							>
								<ViewHeadlineIcon
									sx={{
										width: '14.4px',
										height: '14.4px',
										color: density === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
									}}
								/>
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>

					<AnimatedDataGridPro
						slots={{ columnMenu: CustomColumnMenu }}
						slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
						rows={rows}
						columns={columns}
						density={density}
						rowHeight={density === 'compact' ? 32 : 40}
						columnHeaderHeight={40}
						autoHeight
						disableRowSelectionOnClick
						pagination
						paginationModel={paginationModel}
						onPaginationModelChange={setPaginationModel}
						pageSizeOptions={[10]}
						rowCount={totalRows}
						localeText={GRID_LOCALE_TEXT}
						sx={[tableSx, dataGridDensityTransitionSx]}
					/>
				</Box>

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						gap: '12px',
						padding: '8px 16px 12px',
					}}
				>
					<CustomButton
						variant='contained'
						onClick={closeModal}
						sx={popupActionButtonSx}
					>
						Fechar
					</CustomButton>
				</Box>
			</Box>
		</Dialog>
	)
}

export default CustomModalRestAfterWork
